import  { useState, useEffect } from "react";
import { Box, IconButton, Typography, CircularProgress } from "@mui/material";
import { styled } from "@mui/system";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";

const UploadContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hasImage" && prop !== "disabled",
})(({ hasImage, disabled }) => ({
  width: "250px",
  height: "130px",
  border: `2px dashed ${disabled ? "#ccc" : hasImage ? "#2a9941" : "#d2d6da"}`,
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  cursor: disabled ? "not-allowed" : "pointer",
  position: "relative",
  overflow: "hidden",
  backgroundColor: disabled ? "#f0f0f0" : "transparent",
  transition: "all 0.3s ease-in-out",
}));

const ImagePreview = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "fill",
  objectPosition: "center",
  borderRadius: "10px",
});

const HiddenInput = styled("input")({
  display: "none",
});

const ImageSelector = ({ onImageSelect, imageUrl, disabled ,isDelete=true}) => {
  const [previewUrl, setPreviewUrl] = useState(imageUrl || null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setPreviewUrl(imageUrl);
  }, [imageUrl]);

  const handleFileChange = (event) => {
    if (disabled) return;
    const file = event.target.files[0];

    if (file) {
      const fileSizeLimit = 1 * 1024 * 1024;

      if (file.size > fileSizeLimit) {
        Swal.fire({
          icon: "error",
          title: "File too large!",
          text: "Please upload an image smaller than 1MB.",
          confirmButtonColor: "#d33",
        });
        return;
      }

      if (file.type.startsWith("image/")) {
        setIsLoading(true);
        const objectUrl = URL.createObjectURL(file);
        const img = new Image();
        img.src = objectUrl;

        img.onload = () => {
          setSelectedFile(file);
          setPreviewUrl(objectUrl);
          onImageSelect(file);
          setIsLoading(false);
        };

        img.onerror = () => {
          setIsLoading(false);
          Swal.fire({
            icon: "error",
            title: "Invalid image",
            text: "Something went wrong while loading the image.",
          });
        };
      }
    }
  };

  const handleRemoveImage = (e) => {
    if (disabled) return;
    e.stopPropagation();
    setSelectedFile(null);
    setPreviewUrl(null);
    onImageSelect(null);
  };

  return (
    <Box>
      <UploadContainer
        onClick={() =>
          !disabled && document.getElementById("image-input").click()
        }
        disabled={disabled}
        hasImage={!!previewUrl}
      >
        {isLoading ? (
          <CircularProgress sx={{ color: "#2a9941" }} />
        ) : previewUrl ? (
          <>
            <ImagePreview src={previewUrl} alt="Selected" />
            {(!disabled && isDelete) && (
              
              <IconButton
                sx={{
                  position: "absolute",
                  top: 5,
                  right: 5,

                  background: "#2a9941",
                  color: "#fff",
                  "&:hover": { background: "#1ca444" },
                }}
                onClick={handleRemoveImage}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </>
        ) : (
          <>
            <PhotoCameraIcon
              sx={{ fontSize: 50, color: disabled ? "#aaa" : "#2a9941" }}
            />
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: disabled ? "#aaa" : "#666",
                textAlign: "center",
              }}
            >
              {disabled ? "Disabled" : "Click to select an image"} <br />
              {!disabled && (
                <Typography
                  variant="caption"
                  sx={{ mt: 1, color: "#666", textAlign: "center" }}
                >
                  Max file size: 1MB
                </Typography>
              )}
            </Typography>
          </>
        )}
      </UploadContainer>

      <HiddenInput
        id="image-input"
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        disabled={disabled}
      />
    </Box>
  );
};

export default ImageSelector;
